import { TableSkeleton } from "~/components/ui/skeletons";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getWorkingDaysInfo } from "~/utils/workingDays";

import {
  type AnalysisViewMode,
  MONTH_LABELS,
  SellerMonthlyGoals,
  computeYearlyAggregates,
} from "../types/salesGoals";
import ScrollableTableWrapper from "./ScrollableTableWrapper";
import SellerAnalysisTable from "./SellerAnalysisTable";

interface SellerTabProps {
  sellers: SellerMonthlyGoals[];
  currentMonth: number;
  isLoading: boolean;
  viewMode: AnalysisViewMode;
  selectedMonth: number;
  year: number;
}

function formatCLP(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("es-CL");
}

function formatPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

function pctColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-orange-500";
  return "text-red-500";
}

export default function SellerTab({
  sellers,
  currentMonth,
  isLoading,
  viewMode,
  selectedMonth,
  year,
}: SellerTabProps) {
  if (viewMode === "monthly") {
    return (
      <SellerAnalysisTable
        sellers={sellers}
        selectedMonth={selectedMonth}
        year={year}
        isLoading={isLoading}
      />
    );
  }
  if (isLoading) {
    return <TableSkeleton rows={8} columns={8} />;
  }

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentMonthWd =
    currentMonth > 0 ? getWorkingDaysInfo(year, currentMonth) : undefined;

  const columnTotals: Record<number, number> = {};
  const columnActuals: Record<number, number> = {};
  let grandTotal = 0;
  let grandActual = 0;
  for (const s of sellers) {
    for (const m of months) {
      columnTotals[m] = (columnTotals[m] || 0) + (s.months[m] || 0);
      columnActuals[m] = (columnActuals[m] || 0) + (s.actuals[m] || 0);
    }
    grandTotal += s.total;
    grandActual += s.totalActual;
  }

  return (
    <ScrollableTableWrapper>
      <TableHeader>
        <TableRow>
          <TableHead className="sticky left-0 z-10 min-w-[180px] whitespace-nowrap bg-card">
            Vendedor
          </TableHead>
          {months.map((m) => (
            <TableHead
              key={m}
              className="min-w-[100px] whitespace-nowrap text-right"
            >
              {MONTH_LABELS[m]}
            </TableHead>
          ))}
          <TableHead className="min-w-[120px] whitespace-nowrap text-right font-bold">
            Total
          </TableHead>
          <TableHead className="min-w-[140px] whitespace-nowrap text-right font-bold">
            Deb. Llevar
          </TableHead>
          <TableHead className="min-w-[70px] whitespace-nowrap text-right font-bold">
            %
          </TableHead>
          <TableHead className="min-w-[140px] whitespace-nowrap text-right font-bold">
            Cierre Est.
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sellers.map((seller, index) => {
          const agg = computeYearlyAggregates(
            seller.months,
            seller.actuals,
            year,
            currentMonth,
            currentMonthWd
          );
          return (
            <TableRow
              key={seller.seller_id || "unassigned"}
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
            >
              <TableCell className="sticky left-0 z-10 bg-card font-medium">
                {seller.seller_name}
              </TableCell>
              {months.map((m) => {
                const goal = seller.months[m] || 0;
                const actual = seller.actuals[m] || 0;
                const showActual =
                  m <= currentMonth && (goal > 0 || actual > 0);
                return (
                  <TableCell key={m} className="text-right tabular-nums">
                    <div>
                      <span className="mr-1 text-xs text-muted-foreground">
                        Meta:
                      </span>
                      {goal ? formatCLP(goal) : "-"}
                    </div>
                    {showActual && (
                      <div
                        className={`text-xs ${
                          actual >= goal
                            ? "text-emerald-600"
                            : "text-orange-500"
                        }`}
                      >
                        <span className="mr-1 text-muted-foreground">
                          Real:
                        </span>
                        {formatCLP(actual)}
                      </div>
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="text-right font-semibold tabular-nums">
                <div>
                  <span className="mr-1 text-xs font-normal text-muted-foreground">
                    Meta:
                  </span>
                  {formatCLP(seller.total)}
                </div>
                {currentMonth > 0 && (
                  <div
                    className={`text-xs ${
                      seller.totalActual >= seller.total
                        ? "text-emerald-600"
                        : "text-orange-500"
                    }`}
                  >
                    <span className="mr-1 font-normal text-muted-foreground">
                      Real:
                    </span>
                    {formatCLP(seller.totalActual)}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {currentMonth > 0 ? formatCLP(agg.deberiaLlevar) : "-"}
              </TableCell>
              <TableCell
                className={`text-right font-semibold tabular-nums ${currentMonth > 0 ? pctColor(agg.pctAchievement) : ""}`}
              >
                {currentMonth > 0 ? formatPct(agg.pctAchievement) : "-"}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCLP(agg.cierreEstimado)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell className="sticky left-0 z-10 font-bold">Total</TableCell>
          {months.map((m) => {
            const goal = columnTotals[m] || 0;
            const actual = columnActuals[m] || 0;
            const showActual = m <= currentMonth && (goal > 0 || actual > 0);
            return (
              <TableCell key={m} className="text-right font-bold tabular-nums">
                <div>
                  <span className="mr-1 text-xs font-normal text-muted-foreground">
                    Meta:
                  </span>
                  {goal ? formatCLP(goal) : "-"}
                </div>
                {showActual && (
                  <div
                    className={`text-xs font-medium ${
                      actual >= goal ? "text-emerald-600" : "text-orange-500"
                    }`}
                  >
                    <span className="mr-1 font-normal text-muted-foreground">
                      Real:
                    </span>
                    {formatCLP(actual)}
                  </div>
                )}
              </TableCell>
            );
          })}
          <TableCell className="text-right font-bold tabular-nums">
            <div>
              <span className="mr-1 text-xs font-normal text-muted-foreground">
                Meta:
              </span>
              {formatCLP(grandTotal)}
            </div>
            {currentMonth > 0 && (
              <div
                className={`text-xs font-medium ${
                  grandActual >= grandTotal
                    ? "text-emerald-600"
                    : "text-orange-500"
                }`}
              >
                <span className="mr-1 font-normal text-muted-foreground">
                  Real:
                </span>
                {formatCLP(grandActual)}
              </div>
            )}
          </TableCell>
          {(() => {
            const grandAgg = computeYearlyAggregates(
              columnTotals,
              columnActuals,
              year,
              currentMonth,
              currentMonthWd
            );
            return (
              <>
                <TableCell className="text-right font-bold tabular-nums">
                  {currentMonth > 0 ? formatCLP(grandAgg.deberiaLlevar) : "-"}
                </TableCell>
                <TableCell
                  className={`text-right font-bold tabular-nums ${currentMonth > 0 ? pctColor(grandAgg.pctAchievement) : ""}`}
                >
                  {currentMonth > 0 ? formatPct(grandAgg.pctAchievement) : "-"}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">
                  {formatCLP(grandAgg.cierreEstimado)}
                </TableCell>
              </>
            );
          })()}
        </TableRow>
      </TableFooter>
    </ScrollableTableWrapper>
  );
}
